/**
 * Cliente API para comunicación con el backend
 */

const API_BASE = '/api'

class ApiClient {
  constructor() {
    this.sessionId = localStorage.getItem('sessionId')
  }

  /**
   * Obtener headers con sesión (las cookies se envían automáticamente)
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    }
    
    // El sessionId en header es opcional ahora, las cookies tienen prioridad
    if (this.sessionId) {
      headers['x-session-id'] = this.sessionId
    }
    
    return headers
  }

  /**
   * Login
   */
  async login(username, password) {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Importante: incluir cookies
      body: JSON.stringify({ username, password })
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Error en login')
    }
    
    // Guardar sesión en localStorage como backup
    this.sessionId = data.sessionId
    localStorage.setItem('sessionId', data.sessionId)
    localStorage.setItem('user', JSON.stringify(data.user))
    
    return data
  }

  /**
   * Logout
   */
  async logout() {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include' // Importante: incluir cookies
      })
    } finally {
      // Limpiar sesión local siempre
      this.sessionId = null
      localStorage.removeItem('sessionId')
      localStorage.removeItem('user')
    }
  }

  /**
   * Verificar sesión
   */
  async verify() {
    const response = await fetch(`${API_BASE}/auth/verify`, {
      headers: this.getHeaders(),
      credentials: 'include' // Importante: incluir cookies
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      // Sesión inválida
      this.sessionId = null
      localStorage.removeItem('sessionId')
      localStorage.removeItem('user')
      throw new Error(data.error || 'Sesión inválida')
    }
    
    // Actualizar usuario en localStorage
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user))
    }
    
    return data
  }

  /**
   * Cargar asset JSON
   */
  async loadAsset(filename) {
    const response = await fetch(`${API_BASE}/assets/${filename}`, {
      headers: this.getHeaders(),
      credentials: 'include' // Importante: incluir cookies
    })
    
    if (!response.ok) {
      throw new Error(`Error al cargar ${filename}`)
    }
    
    return response.json()
  }

  /**
   * Obtener usuario actual
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  }

  /**
   * Verificar si hay sesión activa
   */
  isAuthenticated() {
    return !!this.sessionId
  }

  /**
   * Guardar datos en el servidor
   */
  async saveToServer(data) {
    const response = await fetch(`${API_BASE}/saves`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include', // Importante: incluir cookies
      body: JSON.stringify(data)
    })
    
    const result = await response.json()
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al guardar en servidor')
    }
    
    return result
  }

  /**
   * Listar guardados del usuario
   */
  async listSaves() {
    const response = await fetch(`${API_BASE}/saves`, {
      headers: this.getHeaders(),
      credentials: 'include' // Importante: incluir cookies
    })
    
    const result = await response.json()
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al listar guardados')
    }
    
    return result.saves
  }

  /**
   * Obtener guardados (alias para compatibilidad)
   */
  async getSaves() {
    const response = await fetch(`${API_BASE}/saves`, {
      headers: this.getHeaders(),
      credentials: 'include' // Importante: incluir cookies
    })
    
    const result = await response.json()
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al obtener guardados')
    }
    
    return result // Retorna el objeto completo con { saves: [...] }
  }

  /**
   * Cargar un guardado específico
   */
  async loadSave(timestamp, username) {
    const url = username 
      ? `${API_BASE}/saves/${timestamp}?username=${encodeURIComponent(username)}`
      : `${API_BASE}/saves/${timestamp}`
      
    const response = await fetch(url, {
      headers: this.getHeaders(),
      credentials: 'include' // Importante: incluir cookies
    })
    
    const result = await response.json()
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al cargar guardado')
    }
    
    return result.data
  }

  /**
   * Eliminar un guardado
   */
  async deleteSave(timestamp, username) {
    const url = username 
      ? `${API_BASE}/saves/${timestamp}?username=${encodeURIComponent(username)}`
      : `${API_BASE}/saves/${timestamp}`
      
    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include' // Importante: incluir cookies
    })
    
    const result = await response.json()
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al eliminar guardado')
    }
    
    return result
  }

  /**
   * Descargar un guardado como ZIP
   */
  async downloadSaveAsZip(timestamp, username) {
    const url = username 
      ? `${API_BASE}/saves/${timestamp}/download?username=${encodeURIComponent(username)}`
      : `${API_BASE}/saves/${timestamp}/download`
      
    const response = await fetch(url, {
      headers: this.getHeaders(),
      credentials: 'include' // Importante: incluir cookies
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Error al descargar' }))
      throw new Error(error.error || 'Error al descargar guardado')
    }
    
    // Retornar el blob para que el frontend lo descargue
    return response.blob()
  }

  async downloadLastSaveAsZip() {
    const response = await fetch(`${API_BASE}/saves/last/download`, {
      headers: this.getHeaders(),
      credentials: 'include' // Importante: incluir cookies
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Error al descargar' }))
      throw new Error(error.error || 'Error al descargar guardado')
    }
    
    // Retornar el blob para que el frontend lo descargue
    return response.blob()
  }
}

export default new ApiClient()
